import React, { Component } from "react";
import PropTypes from "prop-types";
import ReactQuill, { Quill } from "react-quill";
import shortid from "shortid";
import classnames from "classnames";

import CustomToolbar from "./CustomToolbar";
import {
  fonts as availableFonts,
  sizes as normalSizes,
  formats
} from "settings/Quill/Quill";
import "./Editor.scss";

class Editor extends Component {
  constructor(props) {
    super(props);

    Quill.register(Quill.import("attributors/style/background"), true);
    Quill.register(Quill.import("attributors/style/color"), true);
    Quill.register(Quill.import("attributors/style/align"), true);

    const fonts = Quill.import("attributors/style/font");
    fonts.whitelist = availableFonts;
    Quill.register(fonts, true);

    const sizes = Quill.import("attributors/style/size");
    sizes.whitelist = normalSizes;
    Quill.register(sizes, true);

    const { withImage, withVideo, withLink } = props;

    const toolbarID = shortid.generate();

    const formatsToUse = [...formats];

    const Block = Quill.import("blots/block");
    Block.tagName = "P";
    Quill.register(Block, true);

    const modules = {
      clipboard: {
        matchers: [["image", null]],
        matchVisual: false
      },
      toolbar: {
        container: `[data-toolbar-id="${toolbarID}"]`
      }
    };

    if (withImage || withVideo) {
      formatsToUse.push("imageResize");
      modules.imageResize = {
        parchment: Quill.import("parchment")
      };
    }

    withImage && formatsToUse.push("image");
    withVideo && formatsToUse.push("video");
    withLink && formatsToUse.push("link");
    formatsToUse.push("script");

    this.state = {
      editorHtml: "",
      toolbarID,
      formats: formatsToUse,
      modules,
    };

    this.savedRange = null;
    this.editorRef = React.createRef();
    this.onSelectionChange = this.onSelectionChange.bind(this);
    this.onToolbarSelectMouseDown = this.onToolbarSelectMouseDown.bind(this);
  }

  componentDidMount() {
    const { initialValue } = this.props;

    if (initialValue) {
      this.setState({
        editorHtml: initialValue
      });
    }

    const editor = this.getEditor();
    if (editor) {
      editor.on('selection-change', this.onSelectionChange);
    } else {
      console.warn('Quill editor is not ready in componentDidMount');
    }

    const toolbarNode = document.querySelector(`[data-toolbar-id="${this.state.toolbarID}"]`);
    if (toolbarNode) {
      toolbarNode.querySelectorAll('span').forEach(sel => {
        sel.addEventListener('mousedown', this.onToolbarSelectMouseDown);
      });
    }

    setTimeout(() => {
      editor.root.addEventListener("paste", () => {
        console.log("Paste: ", this.savedRange);        
        this.rangeRestoreHandler(editor);
      });
    }, 0);
  }

  componentWillUnmount() {
    const editor = this.getEditor();
    if (editor) {
      editor.off('selection-change', this.onSelectionChange);
    }

    const toolbarNode = document.querySelector(`[data-toolbar-id="${this.state.toolbarID}"]`);
    if (toolbarNode) {
      toolbarNode.querySelectorAll('span').forEach(sel => {
        sel.removeEventListener('mousedown', this.onToolbarSelectMouseDown);
      });
    }
  }

  setEditorContent(content) {
    this.setState({
      editorHtml: content,
    });
  }

  componentDidUpdate(_prevProps, prevState) {
    const { changeHandlerRaw } = this.props;
    if (changeHandlerRaw && (this.state.editorHtml !== prevState.editorHtml)) {
      const raw = this.getText().trim();
      changeHandlerRaw(raw);
    }
  }  

  getEditor = () => {
    const editor = this.editorRef.current && this.editorRef.current.getEditor();
    return editor;
  };

  getText = () => {
    const editor = this.getEditor();
    return editor.getText();
  };

  handleChange = html => {
    const { editorHtml } = this.state;
    if (editorHtml === html) return;

    // console.log(html)

    this.setState({ editorHtml: html });

    // Replace spaces - prevent collapsing of multiple spaces
    // const formatted = html.replace(/\s/g, "&nbsp;");
    this.props.changeHandler(html);
  };

  onSelectionChange(range/*, oldRange, source */) {
    if (range) {
      this.savedRange = range;     
    } /*else {
      this.savedRange = null;
    }*/
    console.log('Saved range', range);
  }

  onToolbarSelectMouseDown(e) {
    e.preventDefault();

    const editor = this.getEditor();
    if (!editor) return;

    this.rangeRestoreHandler(editor);
  }

  rangeRestoreHandler = editor => {
    editor.focus();

    if (this.savedRange) {
      const maxIndex = Math.max(0, editor.getLength() - 1);
      const index = Math.min(this.savedRange.index, maxIndex);
      const length = this.savedRange.length || 0;

      setTimeout(() => {
        try {
          editor.setSelection(index, length);
        } catch (err) {
          console.warn('Failed to restore selection', err);
        }
      }, 0);
    }
  }

  render() {
    const { editorHtml, toolbarID, modules, formats } = this.state;
    const {
      overflowHidden = false,
      //translatorReadOnly,
      className,
      classes = {},
      onFocus,
      onBlur,
      withImage,
      withVideo,
      style = {},
      withLink,
      withAddAudio,
      readOnly = false
    } = this.props;

    return (
      <div
        className={classnames("custom-editor", {
          overflowHidden
        })}
      >
        <div className={classes.toolbar}>
          <CustomToolbar
            id={toolbarID}
            withImage={withImage}
            withVideo={withVideo}
            withLink={withLink}
            withAddAudio={withAddAudio}
            onBeforeAction={this.restoreSelection}
            editor={this.getEditor()}
          />
        </div>
        <ReactQuill
          ref={this.editorRef}
          style={style}
          //readOnly={translatorReadOnly ? translatorReadOnly : readOnly}
          readOnly={readOnly}
          theme={"snow"}
          onChange={this.handleChange}
          value={editorHtml}
          modules={modules}
          formats={formats}
          className={className}
          onFocus={onFocus}
          onBlur={onBlur}
          preserveWhitespace
        />
      </div>
    );
  }
}

Editor.defaultProps = {
  withImage: true,
  withVideo: true,
  withLink: true
};

Editor.propTypes = {
  initialValue: PropTypes.string,
  changeHandler: PropTypes.func.isRequired,
  className: PropTypes.string,
  //translatorReadOnly: PropTypes.bool,
};

export default Editor;
